import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText, { ListItemTextProps } from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import MenuIcon from '@mui/icons-material/Menu';
import HubIcon from '@mui/icons-material/Hub';
import HomeIcon from '@mui/icons-material/Home';
import GroupIcon from '@mui/icons-material/Group';
import HelpIcon from '@mui/icons-material/Help';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import CurrencyPoundIcon from '@mui/icons-material/CurrencyPound';
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction';
import GridViewIcon from '@mui/icons-material/GridView';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link, useLocation } from 'react-router';
import DevtankLogoComponent from '../imgs/devtank_logo.tsx';
import OSMLogoComponent from '../imgs/osm-logo.tsx';
import Button from '@mui/material/Button';
import type { User } from 'oidc-client-ts';
import { logout } from '../auth/authService.ts';

const BRAND_GREEN = '#1D9E75';

interface CustomListItemTextProps extends ListItemTextProps {
  primary?: string;
}

export function CustomListItemText({ ...props }: CustomListItemTextProps) {
  return (
    <ListItemText
      {...props}
      sx={{
        '& .MuiListItemText-primary': {
          fontFamily: "'Syne', sans-serif",
          fontSize: 14,
          fontWeight: 500,
          color: 'var(--color-text-primary)',
        },
      }}
    />
  );
}

interface NavItemProps {
  to?: string;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  onClick: () => void;
  external?: boolean;
}

function NavItem({ to, icon, label, disabled, onClick, external }: NavItemProps) {
  const location = useLocation();
  const isActive = to ? location.pathname === to : false;

  const linkProps = external
    ? {}
    : to && !disabled
    ? { component: Link, to }
    : {};

  return (
    <ListItemButton
      onClick={onClick}
      disabled={disabled}
      {...linkProps}
      sx={{
        borderRadius: 2,
        mx: 1,
        mb: 0.5,
        transition: 'background 0.15s ease',
        background: isActive ? 'rgba(29,158,117,0.12)' : 'transparent',
        '&:hover': {
          background: isActive
            ? 'rgba(29,158,117,0.18)'
            : 'rgba(255,255,255,0.05)',
        },
        '& .MuiListItemIcon-root': {
          color: isActive ? BRAND_GREEN : 'var(--color-text-secondary)',
          minWidth: 38,
          transition: 'color 0.15s ease',
        },
        '&:hover .MuiListItemIcon-root': {
          color: BRAND_GREEN,
        },
      }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <CustomListItemText primary={label} />
      {isActive && (
        <Box sx={{
          width: 3,
          height: 18,
          borderRadius: 2,
          background: BRAND_GREEN,
          ml: 1,
          flexShrink: 0,
        }} />
      )}
    </ListItemButton>
  );
}

interface DrawerProps {
  user: User | null;
  isAdmin: boolean;
}

export default function TemporaryDrawer({ user, isAdmin }: DrawerProps) {
  const [open, setOpen] = React.useState(false);
  const close = () => setOpen(false);

  const NAV_ITEMS: NavItemProps[] = [
    { to: '/',               icon: <HomeIcon />,             label: 'Home',           onClick: close },
    { to: '/overview',       icon: <HubIcon />,              label: 'Overview',       onClick: close },
    { to: '/sensor-status',  icon: <OnlinePredictionIcon />, label: 'Status',         onClick: close },
    { to: '/dashboard-list', icon: <GridViewIcon />,         label: 'Dashboard List', onClick: close },
    { to: '/users',          icon: <GroupIcon />,            label: 'Users',          onClick: close, disabled: !isAdmin },
    { to: '/alerts',         icon: <AddAlertIcon />,         label: 'Alerts',         onClick: close, disabled: !isAdmin },
    { to: '/tariffs',        icon: <CurrencyPoundIcon />,    label: 'Tariffs',        onClick: close, disabled: !isAdmin },
    {
      icon: <HelpIcon />,
      label: 'User Manual',
      onClick: () => { close(); window.open('/skylight_user_manual.html', '_blank'); },
      external: true,
    },
  ];

  const DrawerList = (
    <Box sx={{
      width: 260,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'space-between',
      py: 1.5,
      background: 'var(--color-bg-secondary)',
    }}>
      {/* Logo */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, mb: 2 }}>
          <OSMLogoComponent />
        </Box>

        <Divider sx={{ borderColor: 'var(--color-border-primary, rgba(255,255,255,0.08))', mb: 1 }} />

        <List component="nav" sx={{ px: 0.5 }}>
          {NAV_ITEMS.map(item => (
            <NavItem key={item.label} {...item} />
          ))}
        </List>
      </Box>

      {/* Bottom logo */}
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, pt: 2 }}>
        <DevtankLogoComponent />
      </Box>
    </Box>
  );

  return (
    <Box
      component="header"
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        py: 0.75,
        background: 'var(--color-bg-secondary)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.06), 0 4px 12px rgba(0,0,0,0.15)',
        mb: '20px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Left — hamburger */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <MenuIcon
          data-testid="menu-button"
          onClick={() => setOpen(true)}
          sx={{
            fontSize: 26,
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            transition: 'transform 0.25s ease, color 0.2s ease',
            '&:hover': {
              transform: 'rotate(90deg) scale(1.1)',
              color: BRAND_GREEN,
            },
          }}
        />
      </Box>

      {/* Centre — title */}
      <Link
        to="/"
        style={{ textDecoration: 'none' }}
      >
        <Box component="span" sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: '-0.5px',
          userSelect: 'none',
        }}>
          <Box component="span" sx={{ color: BRAND_GREEN }}>sky</Box>
          <Box component="span" sx={{ color: 'var(--color-text-primary, #fff)' }}>light</Box>
        </Box>
      </Link>

      {/* Right — logout */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<LogoutIcon sx={{ fontSize: '14px !important' }} />}
        onClick={() => logout()}
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'var(--color-text-secondary)',
          borderColor: 'var(--color-border-primary, rgba(255,255,255,0.15))',
          borderRadius: 2,
          px: 1.5,
          py: 0.5,
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: BRAND_GREEN,
            color: BRAND_GREEN,
            background: 'rgba(29,158,117,0.06)',
          },
        }}
      >
        Logout
      </Button>

      <Drawer
        open={open}
        onClose={close}
        PaperProps={{
          sx: { background: 'var(--color-bg-secondary)', boxShadow: '4px 0 24px rgba(0,0,0,0.3)' },
        }}
      >
        {DrawerList}
      </Drawer>
    </Box>
  );
}
